import { useState } from 'react';
import { api, getApiErrorMessage } from '@/services/api';
import { useToast } from '@/hooks/useToast';
import { useTheme } from '@/hooks/useTheme';
import ForecastGenerationSummaryWidget from './ForecastGenerationSummaryWidget';
import DailyHourlyForecastWidget from './DailyHourlyForecastWidget';

const FORECAST_TYPES = [
  { key: 'day_ahead', label: 'Day Ahead' },
  { key: 'intraday', label: 'Intraday' },
  { key: 'real_time', label: 'Real Time' },
  { key: 'monthly', label: 'Monthly' },
];

interface ForecastingConfig {
  id: string;
  forecastProvider: string;
  forecastType: string;
  updateFrequency: string;
  forecastHorizon: string;
  responsibleParty: string;
}

interface Props {
  assetId: string;
  configs?: ForecastingConfig[];
  onSaved: () => void;
}

type ForecastTab = 'generation' | 'configs';

export default function TabForecasting({ assetId, configs = [], onSaved }: Props) {
  const { addToast } = useToast();
  const { isDark } = useTheme();
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(configs.length === 0);
  const [activeForecastTab, setActiveForecastTab] = useState<ForecastTab>('generation');
  const [form, setForm] = useState({
    forecastProvider: '',
    forecastType: '',
    updateFrequency: '',
    forecastHorizon: '',
    responsibleParty: '',
  });

  const handleAdd = async () => {
    if (!form.forecastProvider || !form.forecastType) {
      addToast('Forecast provider and type are required', 'warning');
      return;
    }
    setSaving(true);
    setApiError(null);
    try {
      const payload: Record<string, unknown> = {
        forecastProvider: form.forecastProvider,
        forecastType: form.forecastType,
      };
      if (form.updateFrequency) payload.updateFrequency = form.updateFrequency;
      if (form.forecastHorizon) payload.forecastHorizon = form.forecastHorizon;
      if (form.responsibleParty) payload.responsibleParty = form.responsibleParty;

      await api.post(`/assets/${assetId}/forecasting-configs`, payload);
      addToast('Forecasting configuration added successfully', 'success');
      setForm({ forecastProvider: '', forecastType: '', updateFrequency: '', forecastHorizon: '', responsibleParty: '' });
      setShowForm(false);
      onSaved();
    } catch (err) {
      const message = getApiErrorMessage(err, 'Failed to add forecasting config');
      setApiError(message);
      addToast(message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';
  const inputBg = isDark ? '#1A1A1A' : '#fff';
  const inputBorder = isDark ? 'rgba(255,255,255,0.1)' : '#E5E5E8';
  const inputClass = 'w-full px-3 py-2 text-sm rounded-md outline-none transition-colors';
  const inputStyle = {
    backgroundColor: inputBg,
    border: `1px solid ${inputBorder}`,
    color: textMain,
  };
  const sectionBg = isDark ? 'rgba(255,255,255,0.03)' : '#F5F5F7';
  const sectionBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const cardBg = isDark ? '#1A1A1A' : '#fff';

  const FORECAST_TYPE_LABELS: Record<string, string> = {
    day_ahead: 'Day Ahead',
    intraday: 'Intraday',
    real_time: 'Real Time',
    monthly: 'Monthly',
  };

  const SUB_TABS: { key: ForecastTab; label: string; icon: string }[] = [
    { key: 'generation', label: 'Forecast Generation', icon: 'ri-bar-chart-box-line' },
    { key: 'configs', label: 'Configurations', icon: 'ri-settings-3-line' },
  ];

  return (
    <div>
      {/* Sub-tabs */}
      <div className="flex items-center gap-0 mb-5" style={{ borderBottom: '1px solid #E5E5E8' }}>
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveForecastTab(tab.key)}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium cursor-pointer transition-colors whitespace-nowrap relative"
            style={{
              color: activeForecastTab === tab.key ? '#D12229' : '#6B6B70',
              borderBottom: activeForecastTab === tab.key ? '2px solid #D12229' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            <i className={`${tab.icon} text-sm`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Forecast Generation tab ── */}
      {activeForecastTab === 'generation' && (
        <div className="space-y-5">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(209,34,41,0.08)' }}>
              <i className="ri-bar-chart-box-line text-sm" style={{ color: '#D12229' }}></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold" style={{ color: textMain }}>Forecast Generation</h3>
              <p className="text-xs" style={{ color: '#6B6B70' }}>Latest ML-based generation forecast for this asset</p>
            </div>
          </div>
          <ForecastGenerationSummaryWidget assetId={assetId} />
          <DailyHourlyForecastWidget assetId={assetId} showActualLine />
        </div>
      )}

      {/* ── Configurations tab ── */}
      {activeForecastTab === 'configs' && (
        <div>
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(209,34,41,0.08)' }}>
                <i className="ri-cloud-line text-sm" style={{ color: '#D12229' }}></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: textMain }}>Forecasting Configurations</h3>
                <p className="text-xs" style={{ color: '#6B6B70' }}>ML forecast providers and update schedules</p>
              </div>
            </div>
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer transition-colors whitespace-nowrap"
                style={{ border: '1px solid #D12229', color: '#D12229' }}
              >
                <i className="ri-add-line"></i> Add Config
              </button>
            )}
          </div>

          {/* Existing configs */}
          {configs.length > 0 && (
            <div className="space-y-2 mb-5">
              {configs.map((fc) => (
                <div key={fc.id} className="p-4 rounded-lg" style={{ border: `1px solid ${sectionBorder}`, background: cardBg }}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <i className="ri-cloud-line text-sm" style={{ color: '#D12229' }}></i>
                      <span className="text-sm font-medium" style={{ color: textMain }}>{fc.forecastProvider}</span>
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full" style={{ background: 'rgba(31,91,143,0.1)', color: '#1F5B8F' }}>
                      {FORECAST_TYPE_LABELS[fc.forecastType] ?? fc.forecastType}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs" style={{ color: '#6B6B70' }}>
                    {fc.updateFrequency && <span><i className="ri-time-line mr-1"></i>{fc.updateFrequency}</span>}
                    {fc.forecastHorizon && <span><i className="ri-calendar-line mr-1"></i>Horizon: {fc.forecastHorizon}</span>}
                    {fc.responsibleParty && <span><i className="ri-user-line mr-1"></i>{fc.responsibleParty}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add form */}
          {showForm && (
            <div className="p-5 rounded-xl" style={{ background: sectionBg, border: `1px solid ${sectionBorder}` }}>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold" style={{ color: textMain }}>New Forecasting Config</h4>
                {configs.length > 0 && (
                  <button onClick={() => setShowForm(false)} className="text-xs cursor-pointer" style={{ color: '#6B6B70' }}>
                    Cancel
                  </button>
                )}
              </div>

              {apiError && (
                <div className="mb-4 px-4 py-3 rounded-lg text-sm" style={{ background: 'rgba(209,34,41,0.06)', border: '1px solid rgba(209,34,41,0.2)', color: '#D12229' }}>
                  <i className="ri-error-warning-line mr-2"></i>{apiError}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>
                    Forecast Provider <span style={{ color: '#D12229' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={form.forecastProvider}
                    onChange={(e) => setForm((prev) => ({ ...prev, forecastProvider: e.target.value }))}
                    placeholder="e.g. Helios ML Engine, Solargis"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>
                    Forecast Type <span style={{ color: '#D12229' }}>*</span>
                  </label>
                  <select
                    value={form.forecastType}
                    onChange={(e) => setForm((prev) => ({ ...prev, forecastType: e.target.value }))}
                    className={inputClass}
                    style={inputStyle}
                  >
                    <option value="">Select type...</option>
                    {FORECAST_TYPES.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>Update Frequency</label>
                  <input
                    type="text"
                    value={form.updateFrequency}
                    onChange={(e) => setForm((prev) => ({ ...prev, updateFrequency: e.target.value }))}
                    placeholder="e.g. Every 15min, Hourly, Daily"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>Forecast Horizon</label>
                  <input
                    type="text"
                    value={form.forecastHorizon}
                    onChange={(e) => setForm((prev) => ({ ...prev, forecastHorizon: e.target.value }))}
                    placeholder="e.g. 24h, 48h, 7 days"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B6B70' }}>Responsible Party</label>
                  <input
                    type="text"
                    value={form.responsibleParty}
                    onChange={(e) => setForm((prev) => ({ ...prev, responsibleParty: e.target.value }))}
                    placeholder="e.g. Helios Aggregate LLC"
                    className={inputClass}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  onClick={handleAdd}
                  disabled={saving || !form.forecastProvider || !form.forecastType}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium text-white cursor-pointer transition-colors disabled:opacity-50 whitespace-nowrap"
                  style={{ background: '#D12229' }}
                >
                  {saving ? (
                    <><i className="ri-loader-4-line animate-spin"></i> Adding...</>
                  ) : (
                    <><i className="ri-add-line"></i> Add Config</>
                  )}
                </button>
              </div>
            </div>
          )}

          {configs.length === 0 && !showForm && (
            <div className="text-center py-8">
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: sectionBg }}>
                <i className="ri-cloud-off-line text-xl" style={{ color: '#6B6B70' }}></i>
              </div>
              <p className="text-sm font-medium" style={{ color: textMain }}>No forecasting configs</p>
              <p className="text-xs mt-1" style={{ color: '#6B6B70' }}>Add a forecast provider to enable generation predictions</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
